<template>
  <div class="font-sans bg-[#f6f4f1] text-[#1f2937] min-h-screen flex flex-col">
    <AppHeader />

    <div class="max-w-4xl mx-auto w-full px-4 py-8">
      <h1 class="text-2xl font-bold text-[#152a34] mb-6">Meus Agendamentos</h1>

      <!-- Filtros de status -->
      <div class="flex gap-2 flex-wrap mb-6">
        <button
          v-for="filter in statusFilters"
          :key="filter.value"
          @click="activeStatus = filter.value"
          :class="[
            'px-4 py-1.5 rounded-full text-sm font-medium transition',
            activeStatus === filter.value
              ? 'bg-[#152a34] text-white'
              : 'bg-white border border-gray-200 text-gray-600 hover:border-[#b99a52]'
          ]"
        >{{ filter.label }}</button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="space-y-4">
        <div v-for="i in 3" :key="i" class="bg-white rounded-2xl shadow p-5 animate-pulse">
          <div class="h-4 bg-gray-200 rounded w-1/2 mb-3"></div>
          <div class="h-3 bg-gray-100 rounded w-1/3"></div>
        </div>
      </div>

      <!-- Erro -->
      <div v-else-if="error" class="text-center py-20 text-red-400">
        <span class="mdi mdi-alert-circle text-5xl block mb-3"></span>
        <p class="text-lg font-medium">Erro ao carregar agendamentos</p>
        <p class="text-sm mt-1">{{ error }}</p>
        <button @click="loadPage()" class="mt-4 inline-block px-6 py-2 bg-[#152a34] text-white rounded-xl font-semibold text-sm">
          Tentar novamente
        </button>
      </div>

      <!-- Vazio -->
      <div v-else-if="appointments.length === 0" class="text-center py-20 text-gray-400">
        <span class="mdi mdi-calendar-blank text-5xl block mb-3"></span>
        <p class="text-lg">Nenhum agendamento encontrado.</p>
        <router-link :to="{ name: 'provider-search' }" class="mt-4 inline-block px-6 py-2 bg-[#b99a52] text-white rounded-xl font-semibold text-sm">
          Buscar Profissionais
        </router-link>
      </div>

      <!-- Lista -->
      <div v-else class="space-y-4">
        <router-link
          v-for="appt in appointments"
          :key="appt.id"
          :to="{ name: 'appointment-details', params: { id: appt.id } }"
          class="block bg-white rounded-2xl shadow border border-[#e0dcd6] p-5 hover:border-[#b99a52] transition"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-1">
                <h3 class="font-semibold text-[#152a34] truncate">{{ appt.service?.name }}</h3>
                <span :class="['px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0', statusClass(appt.status)]">
                  {{ statusLabel(appt.status) }}
                </span>
              </div>
              <p class="text-sm text-gray-500">{{ appt.provider?.name }}</p>
              <p class="text-xs text-gray-400 mt-1 flex items-center gap-1">
                <span class="mdi mdi-calendar text-[#b99a52]"></span>
                {{ formatDate(appt.scheduled_at) }}
              </p>
            </div>
            <div class="text-right flex-shrink-0">
              <p class="font-bold text-[#152a34]">R$ {{ formatPrice(appt.service?.price_with_fee) }}</p>
              <p class="text-xs text-gray-400">{{ appt.duration_minutes }} min</p>
            </div>
          </div>
        </router-link>
      </div>

      <!-- Paginação -->
      <div v-if="pagination.last_page > 1" class="flex justify-center mt-8 gap-2">
        <button
          v-for="p in pagination.last_page"
          :key="p"
          @click="loadPage(p)"
          :class="[
            'px-4 py-2 rounded-lg text-sm font-medium transition',
            currentPage === p ? 'bg-[#152a34] text-white' : 'bg-white border border-gray-200 hover:border-[#b99a52]'
          ]"
        >{{ p }}</button>
      </div>
    </div>

    <AppFooter />
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import { getContractorAppointments } from '@/services/api'
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'

const appointments = ref([])
const loading = ref(true)
const error = ref(null)
const currentPage = ref(1)
const pagination = ref({ last_page: 1 })
const activeStatus = ref('')

const statusFilters = [
  { label: 'Todos', value: '' },
  { label: 'Aguardando', value: 'pending' },
  { label: 'Confirmados', value: 'confirmed' },
  { label: 'Em andamento', value: 'in_progress' },
  { label: 'Pagar', value: 'awaiting_payment' },
  { label: 'Concluídos', value: 'completed' },
  { label: 'Cancelados', value: 'cancelled' },
]

function statusLabel (status) {
  const map = { pending: 'Aguardando', confirmed: 'Confirmado', in_progress: 'Em andamento', awaiting_payment: 'Pagar', completed: 'Concluído', cancelled: 'Cancelado' }
  return map[status] || status
}

function statusClass (status) {
  const map = { pending: 'bg-yellow-100 text-yellow-700', confirmed: 'bg-blue-100 text-blue-700', in_progress: 'bg-indigo-100 text-indigo-700', awaiting_payment: 'bg-orange-100 text-orange-700', completed: 'bg-green-100 text-green-700', cancelled: 'bg-red-100 text-red-700' }
  return map[status] || 'bg-gray-100 text-gray-600'
}

function formatDate (d) {
  if (!d) return ''
  return new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
}

function formatPrice (p) {
  return Number(p || 0).toFixed(2).replace('.', ',')
}

async function loadPage (page = 1) {
  loading.value = true
  error.value = null
  currentPage.value = page
  try {
    const params = { page, limit: 15 }
    if (activeStatus.value) params.status = activeStatus.value
    const { data } = await getContractorAppointments(params)
    console.log('[AppointmentList] response:', data)
    appointments.value = data.data || []
    pagination.value = { last_page: data.lastPage || 1 }
  } catch (err) {
    const msg = err.response?.data?.error || err.response?.data?.message || err.message
    error.value = msg || 'Erro desconhecido'
    appointments.value = []
    console.error('[AppointmentList] erro:', err.response?.status, msg)
  } finally {
    loading.value = false
  }
}

onMounted(() => loadPage())
watch(activeStatus, () => loadPage(1))
</script>
